import clsx from "clsx"
import type { FC, PropsWithChildren, ReactNode } from "react"
import { useId, useLayoutEffect, useMemo, useRef, useState } from "react"
import styles from "./style.module.scss"

export interface IOutlinedText {
  text?: string
  className?: string
  strokeWidthEm?: number
  strokeColor?: string
  gradientFrom?: string
  gradientTo?: string
}

export const OutlinedTextV4: FC<PropsWithChildren<IOutlinedText>> = ({
  children,
  text,
  className,
  strokeWidthEm = 0.25,
  strokeColor = "#352626",
  gradientFrom = "#FCA71F",
  gradientTo = "#FFFFFF",
  ...rest
}) => {
  const getText = (node: ReactNode): string => {
    if (node === null || node === false) return ""
    if (typeof node === "string" || typeof node === "number")
      return String(node)
    if (Array.isArray(node)) return node.map(getText).join("")
    if (typeof node === "object" && node && "props" in (node as any)) {
      return getText((node as any).props?.children)
    }
    return ""
  }

  const content = (text ?? getText(children)).trim()

  const rid = useId()
  const uid = useMemo(() => rid.replace(/:/g, ""), [rid])

  const measureRef = useRef<HTMLSpanElement | null>(null)
  const [box, setBox] = useState<{
    width: number
    height: number
    fontSize: number
    fontFamily: string
    fontWeight: string
  } | null>(null)

  useLayoutEffect(() => {
    const el = measureRef.current
    if (!el) return

    const read = () => {
      const rect = el.getBoundingClientRect()
      const cs = window.getComputedStyle(el)

      setBox({
        width: Math.ceil(rect.width),
        height: Math.ceil(rect.height),
        fontSize: parseFloat(cs.fontSize) || 12,
        fontFamily: cs.fontFamily,
        fontWeight: cs.fontWeight
      })
    }

    read()
    const ro = new ResizeObserver(read)
    ro.observe(el)
    return () => ro.disconnect()
  }, [content])

  if (!box) {
    return (
      <span className={clsx(styles.OutlinedText, className)} {...rest}>
        <span ref={measureRef} className={styles.OutlinedText_text}>
          {content}
        </span>
      </span>
    )
  }

  const y = box.height * 0.85
  const gradId = `grad-${uid}`
  const maskId = `mask-${uid}`
  const strokeWidthPx = box.fontSize * strokeWidthEm

  return (
    <span className={clsx(styles.OutlinedText, className)} {...rest}>
      <span ref={measureRef} className={styles.OutlinedText_text}>
        {content}
      </span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        key={uid}
        className={styles.OutlinedText_svg}
        viewBox={`0 0 ${box.width} ${box.height}`}
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id={gradId}
            gradientUnits="userSpaceOnUse"
            x1={0}
            y1={box.height}
            x2={0}
            y2={0}
          >
            <stop offset="0%" stopColor={gradientFrom} />
            <stop offset="88%" stopColor={gradientTo} />
          </linearGradient>
          <mask id={maskId} maskUnits="userSpaceOnUse">
            <rect
              x="0"
              y="0"
              width={box.width}
              height={box.height}
              fill="black"
            />
            <text
              x="0"
              y={y}
              fontSize={box.fontSize}
              fontFamily={box.fontFamily}
              fontWeight={box.fontWeight}
              fill="white"
            >
              {content}
            </text>
          </mask>
        </defs>
        <text
          x="0"
          y={y}
          fontSize={box.fontSize}
          fontFamily={box.fontFamily}
          fontWeight={box.fontWeight}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidthPx}
          strokeLinejoin="round"
        >
          {content}
        </text>
        <rect
          x="0"
          y="0"
          width={box.width}
          height={box.height}
          fill={`url(#${gradId})`}
          mask={`url(#${maskId})`}
        />
      </svg>
    </span>
  )
}
